/**
 * VowAI Labs - AI Engineering Showcase
 * Portfolio page showcasing VowAI's AI architecture, engineering decisions,
 * and technical implementation details. Reads as a technical narrative.
 * 
 * Story Arc:
 * Act 1: Architecture Overview + Tech Stack (the big picture)
 * Act 2: Multi-Model Engine + Prompt Engineering + RAG (core engine)
 * Act 3: Model Evaluation + A/B Testing + Cost Optimization (measurement)
 * Act 4: VowBot Agent + Vendor Matching + Cultural Intelligence (applied AI)
 * Act 5: Analytics & Feedback Loop (system thinking)
 */
import React from 'react';
import BackgroundIcons from '../../components/BackgroundIcons';
import Section1Architecture from '../../components/VowAILabs/Section1Architecture';
import Section2MultiModel from '../../components/VowAILabs/Section2MultiModel';
import Section3PromptEngineering from '../../components/VowAILabs/Section3PromptEngineering';
import Section4RAG from '../../components/VowAILabs/Section4RAG';
import Section5VowBot from '../../components/VowAILabs/Section5VowBot';
import Section6Evaluation from '../../components/VowAILabs/Section6Evaluation';
import Section7ABTesting from '../../components/VowAILabs/Section7ABTesting';
import Section8CostPerformance from '../../components/VowAILabs/Section8CostPerformance';
import Section9VendorMatching from '../../components/VowAILabs/Section9VendorMatching';
import Section10Cultural from '../../components/VowAILabs/Section10Cultural';
import Section11Analytics from '../../components/VowAILabs/Section11Analytics';
import Section12TechStack from '../../components/VowAILabs/Section12TechStack';

const labsIcons = [
  { emoji: '🧬', size: '4xl', opacity: '15' },
  { emoji: '⚡', size: '4xl', opacity: '15' },
  { emoji: '🔬', size: '4xl', opacity: '15' },
  { emoji: '🧪', size: '4xl', opacity: '15' },
  { emoji: '🤖', size: '4xl', opacity: '15' },
  { emoji: '💎', size: '4xl', opacity: '15' },
];

const VowAILabsPage = () => {

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 relative overflow-hidden">
      <BackgroundIcons icons={labsIcons} />

      <div className="container mx-auto px-4 py-8 relative z-10">

        {/* Page Header */}
        <div className="text-center mb-12 relative">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-indigo-400 via-purple-400 to-violet-400 rounded-full shadow-2xl mb-8 animate-bounce">
            <span className="text-4xl animate-pulse">🧬</span>
          </div>

          <h1 className="text-6xl md:text-7xl font-serif font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 bg-clip-text text-transparent mb-6 tracking-wide">
            VowAI Labs
          </h1>

          <div className="flex items-center justify-center mb-6">
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
            <div className="mx-4 text-3xl animate-bounce">⚡</div>
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
          </div>

          <p className="text-xl text-gray-700 font-light italic mb-8">
            AI architecture, engineering decisions & technical deep dive ✨
          </p>

          <div className="flex items-center justify-center gap-3 flex-wrap">
            <a href="/admin/evaluation" target="_blank" rel="noopener noreferrer"
              className="px-6 py-3 rounded-full font-bold text-sm transition-all duration-300 transform hover:scale-105 shadow-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:shadow-emerald-300/50 hover:shadow-xl">
              <span className="mr-2">📊</span>Model Evaluation
            </a>
            <a href="/admin/ab-testing" target="_blank" rel="noopener noreferrer"
              className="px-6 py-3 rounded-full font-bold text-sm transition-all duration-300 transform hover:scale-105 shadow-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:shadow-amber-300/50 hover:shadow-xl">
              <span className="mr-2">🧪</span>A/B Testing
            </a>
          </div>
        </div>

        {/* Act 1 — "What is this system?" */}
        <Section1Architecture />

        {/* Act 2 — "How does the AI actually work?" */}
        <Section2MultiModel />
        <Section3PromptEngineering />
        <Section4RAG />

        {/* Act 3 — "What intelligent features power the platform?" */}
        <Section5VowBot />
        <Section6Evaluation />
        <Section7ABTesting />
        <Section8CostPerformance />

        {/* Act 4 — "Applied AI in action" */}
        <Section9VendorMatching />
        <Section10Cultural />

        {/* Act 5 — "How does it all connect?" */}
        <Section11Analytics />

        {/* Reference — Tech stack details */}
        <Section12TechStack />

        {/* Footer CTA */}
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-indigo-400 via-purple-400 to-violet-400 rounded-full shadow-2xl mb-6 animate-bounce">
            <span className="text-2xl animate-pulse">🚀</span>
          </div>
          <h2 className="text-3xl font-serif font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 bg-clip-text text-transparent mb-4">
            Built with AI, for Love
          </h2>
          <p className="text-gray-500 font-light italic mb-6">
            Every feature powered by thoughtful AI engineering ✨
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <a href="/wedding-blueprint/dream-wedding-generator" target="_blank" rel="noopener noreferrer"
              className="bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-3 px-8 rounded-2xl font-bold text-sm hover:shadow-2xl transform hover:scale-105 transition-all duration-300 shadow-lg">
              <span className="mr-2 animate-bounce inline-block">🎨</span>Try Dream Wedding Generator
            </a>
            <a href="/wedding-blueprint/moodboard-matcher" target="_blank" rel="noopener noreferrer"
              className="bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 text-white py-3 px-8 rounded-2xl font-bold text-sm hover:shadow-2xl transform hover:scale-105 transition-all duration-300 shadow-lg">
              <span className="mr-2 animate-bounce inline-block">📸</span>Try Moodboard Matcher
            </a>
          </div>
        </div>

      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeInUp { animation: fadeInUp 0.6s ease-out forwards; }
      `}</style>
    </div>
  );
};

export default VowAILabsPage;
